import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ensureOwnListing } from '../../util/data';
import { ListingLink, NamedLink } from '../../components';
import { EditListingLocationForm } from '../../forms';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './EditListingLocationPanel.module.css';
import HelpAndContact from '../HelpAndContact/HelpAndContact';
import { createSlug } from '../../util/urlHelpers';
import axios from 'axios';
import { apiBaseUrl } from '../../util/api';

const { LatLng } = sdkTypes;

class EditListingLocationPanel extends Component {
  constructor(props) {
    super(props);

    this.getInitialValues = this.getInitialValues.bind(this);

    this.state = {
      initialValues: this.getInitialValues(),
    };
  }

  getInitialValues() {
    const { listing } = this.props;
    const currentListing = ensureOwnListing(listing);
    const { geolocation, publicData } = currentListing.attributes;

    // Only render current search if full place object is available in the URL params
    // TODO bounds are missing - those need to be queried directly from Google Places
    const locationFieldsPresent =
      publicData && publicData.location && publicData.location.address && geolocation;
    const location = publicData && publicData.location ? publicData.location : {};
    const { address, building } = location;
    const ProviderAddress = currentListing?.author.attributes.profile.publicData.companyAddress;
    const listingAddress = publicData.listingAddress;
    const isSameAddressAsProfile = publicData.isSameAddressAsProfile;
    return {
      geolocation,
      ProviderAddress,
      listingAddress,
      isSameAddressAsProfile:
        typeof isSameAddressAsProfile === 'string'
          ? [isSameAddressAsProfile]
          : Array.isArray(isSameAddressAsProfile)
            ? isSameAddressAsProfile
            : null,
    };
  }

  render() {
    const {
      className,
      rootClassName,
      listing,
      disabled,
      ready,
      onSubmit,
      onChange,
      submitButtonText,
      panelUpdated,
      updateInProgress,
      errors,
      userNativeLang,
      onManageDisableScrolling,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);
    const currentListing = ensureOwnListing(listing);

    const isPublished =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
    const panelTitle = isPublished ? (
      <FormattedMessage
        id="EditListingLocationPanel.title"
        values={{
          listingTitle: (
            <ListingLink listing={listing}>
              <FormattedMessage id="EditListingLocationPanel.listingTitle" />
            </ListingLink>
          ),
        }}
      />
    ) : (
      <FormattedMessage id="EditListingDescriptionPanel.ListYourJob" />
    );

    return (
      <div className={classes}>
        <HelpAndContact />
        <div className={css.editHeadingDiv}>
          <h1 className={css.title}>{panelTitle}</h1>
          {listing?.id?.uuid && isPublished && (
            <div>
              <NamedLink
                name="ListingPage"
                params={{
                  id: listing?.id?.uuid,
                  slug: createSlug(listing?.attributes?.title || ''),
                }}
              >
                <FormattedMessage id="EditListing.GoToListing" />
              </NamedLink>
            </div>
          )}
        </div>
        <EditListingLocationForm
          className={css.form}
          initialValues={this.state.initialValues}
          onSubmit={async values => {
            const {
              EditlistingAddressCheckBox,
              ProviderAddress,
              city,
              country,
              county,
              listingAddress,
              currentAddress,
              isSameAddressAsProfile,
              lat,
              lng,
            } = values;
            const searchKeys = currentListing?.attributes?.publicData?.searchKeys;
            const keyword = currentListing?.attributes?.publicData?.keyword;
            const splittedAddress = listingAddress?.address.split(', ');
            const createLocation = location => {
              const newlocationArr = [];
              const locationLength = location.length;
              for (let i = 1; i <= locationLength; i++) {
                newlocationArr.push(location.substring(0, i));
              }
              return newlocationArr;
            };

            const updateValues = keyword.includes(
              listingAddress?.address
                .trim()
                .replace(/[,\s]/g, '')
                .toLowerCase()
            )
              ? {
                geolocation: !!lat && !!lng ? new LatLng(parseFloat(lat), parseFloat(lng)) : null,
                publicData: {
                  listingAddress,
                  isSameAddressAsProfile,
                  defaultProviderAddress: {
                    address: ProviderAddress?.address,
                    postalCode: ProviderAddress?.postalCode,
                  },
                  searchPin: [
                    listingAddress?.postalCode
                      .trim()
                      .replace(/[,\s]/g, '')
                      .toLowerCase(),
                  ],
                },
              }
              : {
                geolocation: !!lat && !!lng ? new LatLng(parseFloat(lat), parseFloat(lng)) : null,
                publicData: {
                  listingAddress,
                  isSameAddressAsProfile,
                  defaultProviderAddress: {
                    address: ProviderAddress?.address,
                    postalCode: ProviderAddress?.postalCode,
                  },
                  searchPin: [
                    listingAddress?.postalCode
                      .trim()
                      .replace(/[,\s]/g, '')
                      .toLowerCase(),
                  ],
                  keyword: [
                    ...keyword,
                    ...createLocation(
                      listingAddress?.address
                        .trim()
                        .replace(/[,\s]/g, '')
                        .toLowerCase()
                    ),
                  ],
                },
              };

            await onSubmit(updateValues);
            if (currentListing?.attributes?.state === 'published') {
              await axios
                .patch(
                  `${apiBaseUrl()}/api/timeStatement/updateListingDetails`,
                  {
                    listingId: currentListing?.id?.uuid,
                    providerId: currentListing?.author?.id?.uuid,
                  },
                  { withCredentials: true }
                )
                .catch(err => { });
            }
          }}
          listing={listing}
          onChange={onChange}
          saveActionMsg={submitButtonText}
          disabled={disabled}
          ready={ready}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          fetchErrors={errors}
          userNativeLang={userNativeLang}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </div>
    );
  }
}

const { func, object, string, bool } = PropTypes;

EditListingLocationPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingLocationPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingLocationPanel;